import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Pencil, CheckCircle2, XCircle, RotateCcw, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { AutoTextarea, inputCls } from "./fields";
import { ItemDialog, ObservationsDialog } from "./ProfessionalRecord";

const DIARY_MAIN = [
  ["work_done", "Su cosa avete lavorato?"],
  ["observations", "Cosa hai osservato?"],
  ["progress_outcome", "Com'è andata?"],
  ["next_step", "Qual è il prossimo passo?"],
];
const DIARY_OPT = [
  ["planned_focus", "Focus previsto / obiettivo della seduta"],
  ["difficulties", "Difficoltà o criticità emerse"],
  ["professional_decisions", "Decisioni professionali"],
];
const RELEVANCE = [
  ["unspecified", "Non specificata"],
  ["ordinary", "Ordinaria"],
  ["relevant", "Rilevante"],
  ["turning_point", "Snodo importante"],
];
const DIARY_KEYS = [...DIARY_MAIN, ...DIARY_OPT, ["internal_notes"]].map((x) => x[0]);
const EMPTY = { ...Object.fromEntries(DIARY_KEYS.map((k) => [k, ""])), relevance: "unspecified" };

const DIARY_STATUS = { empty: "Da compilare", draft: "Bozza", completed: "Completato" };
const DIARY_STATUS_CLS = {
  empty: "bg-secondary text-muted-foreground",
  draft: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
};
const SAVE_LABEL = { idle: "", saving: "Salvataggio…", saved: "Salvato", error: "Non salvato" };
const SAVE_CLS = { idle: "text-muted-foreground", saving: "text-amber-600", saved: "text-emerald-600", error: "text-destructive" };

const pick = (obj, keys) => Object.fromEntries(keys.map((k) => [k, obj?.[k] ?? ""]));
const hwValid = (h) => !!((h.title || "").trim() || (h.instructions || "").trim());

const useVVHeight = () => {
  const [h, setH] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const on = () => setH(vv.height);
    on();
    vv.addEventListener("resize", on);
    vv.addEventListener("scroll", on);
    return () => { vv.removeEventListener("resize", on); vv.removeEventListener("scroll", on); };
  }, []);
  return h;
};

// ---- Homework add/edit form (keyboard-aware modal) ----
const HomeworkForm = ({ item, onClose, onSubmit }) => {
  const [f, setF] = useState(() => pick(item || {}, ["title", "instructions", "frequency", "duration", "owner_note"]));
  const [busy, setBusy] = useState(false);
  const vh = useVVHeight();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.title.trim() && !f.instructions.trim()) return toast.error("Inserisci almeno un titolo o le istruzioni.");
    setBusy(true);
    try { await onSubmit(f); onClose(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="homework-form"
        className="top-2 translate-y-0 sm:top-1/2 sm:-translate-y-1/2 w-[calc(100%-1.5rem)] max-w-lg p-0 gap-0 flex flex-col overflow-hidden"
        style={{ maxHeight: vh ? `${vh - 16}px` : "92dvh" }}>
        <DialogHeader className="px-5 pt-5 pb-2 shrink-0"><DialogTitle>{item ? "Modifica compito" : "Nuovo compito"}</DialogTitle></DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-3">
          <div><div className="text-sm font-medium">Titolo</div>
            <input data-testid="hw-title" className={`${inputCls} mt-1`} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div><div className="text-sm font-medium">Istruzioni</div>
            <AutoTextarea testid="hw-instructions" value={f.instructions} onChange={(v) => set("instructions", v)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-sm font-medium">Frequenza</div>
              <input data-testid="hw-frequency" className={`${inputCls} mt-1`} placeholder="es. 2 volte/giorno" value={f.frequency} onChange={(e) => set("frequency", e.target.value)} /></div>
            <div><div className="text-sm font-medium">Durata</div>
              <input data-testid="hw-duration" className={`${inputCls} mt-1`} placeholder="es. 10 min" value={f.duration} onChange={(e) => set("duration", e.target.value)} /></div>
          </div>
          <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3">
            <div className="text-sm font-medium text-sky-900">Nota per il proprietario</div>
            <div className="text-xs text-sky-700/80 mb-1">Contenuto pensato per essere condiviso in futuro con il proprietario.</div>
            <AutoTextarea testid="hw-owner-note" value={f.owner_note} onChange={(v) => set("owner_note", v)} />
          </div>
        </div>
        <div className="shrink-0 border-t border-border bg-background px-5 py-3 flex gap-2 justify-end" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button className="rounded-full" data-testid="hw-save" disabled={busy} onClick={submit}>{busy ? "…" : "Salva"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const HW_STATUS = { active: "Attivo", completed: "Completato", cancelled: "Annullato" };
const HW_STATUS_CLS = { active: "bg-amber-100 text-amber-800", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-secondary text-muted-foreground" };

const HomeworkCard = ({ h, carry, onEdit, onStatus }) => (
  <div className="rounded-xl border border-border bg-card p-3" data-testid={`homework-item-${h.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold break-words">{h.title || "(senza titolo)"}</div>
        {h.instructions && <div className="text-sm text-muted-foreground whitespace-pre-line break-words mt-0.5">{h.instructions}</div>}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
          {h.frequency && <span>Frequenza: {h.frequency}</span>}
          {h.duration && <span>Durata: {h.duration}</span>}
        </div>
        {h.owner_note && <div className="text-xs mt-1 rounded-md bg-sky-50 text-sky-900 px-2 py-1 whitespace-pre-line break-words">Proprietario: {h.owner_note}</div>}
      </div>
      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 shrink-0 ${HW_STATUS_CLS[h.status]}`} data-testid={`hw-status-badge-${h.id}`}>{HW_STATUS[h.status]}</span>
    </div>
    <div className="flex flex-wrap gap-1.5 pt-2">
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`hw-edit-${h.id}`} onClick={() => onEdit(h)}><Pencil className="h-3.5 w-3.5 mr-1" />Modifica</Button>
      {h.status === "active" && <Button size="sm" variant="outline" className="rounded-full" data-testid={`hw-complete-${h.id}`} onClick={() => onStatus(h, "completed")}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Completa</Button>}
      {h.status === "active" && <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" data-testid={`hw-cancel-${h.id}`} onClick={() => onStatus(h, "cancelled")}><XCircle className="h-3.5 w-3.5 mr-1" />Annulla</Button>}
      {h.status !== "active" && <Button size="sm" variant="outline" className="rounded-full" data-testid={`hw-reactivate-${h.id}`} onClick={() => onStatus(h, "active")}><RotateCcw className="h-3.5 w-3.5 mr-1" />Riattiva</Button>}
    </div>
    {carry && <div className="text-[11px] text-muted-foreground mt-1">Da una lezione precedente</div>}
  </div>
);

const RECORD_CHOICES = [
  ["observation", "Osservazione di soglia"],
  ["trigger", "Trigger"],
  ["risk", "Rischio"],
  ["resource", "Risorsa"],
  ["hypothesis", "Ipotesi"],
  ["objective", "Obiettivo"],
  ["strategy", "Strategia"],
];

// ---- Diary -> Professional Record (B2.1) bridge. Reuses existing B2.1 editors. ----
const DiaryToRecord = ({ dogId }) => {
  const [items, setItems] = useState([]);
  const [chooser, setChooser] = useState(false);
  const [itemDlg, setItemDlg] = useState(null);
  const [obsPick, setObsPick] = useState(false);
  const [obsFor, setObsFor] = useState(null);
  const loadItems = useCallback(() => { apiGet(`/dogs/${dogId}/professional-items`).then(({ data }) => setItems(data)).catch(() => {}); }, [dogId]);
  useEffect(() => { loadItems(); }, [loadItems]);
  const activeOpts = (t) => items.filter((i) => i.item_type === t && !i.archived_at).map((i) => ({ id: i.id, title: i.title }));
  const linkOptions = { trigger: activeOpts("trigger"), risk: activeOpts("risk"), objective: activeOpts("objective"), strategy: activeOpts("strategy") };
  const triggers = items.filter((i) => i.item_type === "trigger" && !i.archived_at);
  const choose = (key) => {
    setChooser(false);
    if (key === "observation") {
      if (triggers.length === 0) { toast.error("Crea prima un Trigger nella Scheda professionale."); return; }
      setObsPick(true);
    } else setItemDlg(key);
  };
  return (
    <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="diary-to-record">
      <h4 className="font-head text-base font-bold">Informazioni emerse durante la lezione</h4>
      <p className="text-sm text-muted-foreground mt-1">Se durante questa lezione è emerso qualcosa che deve entrare stabilmente nella Scheda professionale, puoi aggiornarla qui.</p>
      <Button size="sm" variant="outline" className="rounded-full mt-3" data-testid="diary-update-record-btn" onClick={() => { loadItems(); setChooser(true); }}><Plus className="h-4 w-4 mr-1" />Aggiorna Scheda professionale</Button>

      <Dialog open={chooser} onOpenChange={setChooser}>
        <DialogContent data-testid="record-chooser" className="max-w-sm">
          <DialogHeader><DialogTitle>Cosa vuoi aggiungere alla Scheda?</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            {RECORD_CHOICES.map(([k, label]) => (
              <Button key={k} variant="outline" className="justify-start rounded-xl" data-testid={`record-choice-${k}`} onClick={() => choose(k)}>{label}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={obsPick} onOpenChange={setObsPick}>
        <DialogContent data-testid="obs-trigger-pick" className="max-w-sm">
          <DialogHeader><DialogTitle>Per quale trigger?</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            {triggers.map((t) => (
              <Button key={t.id} variant="outline" className="justify-start rounded-xl" data-testid={`obs-trigger-${t.id}`} onClick={() => { setObsPick(false); setObsFor(t); }}>{t.title}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {itemDlg && <ItemDialog type={itemDlg} dogId={dogId} linkOptions={linkOptions} onClose={() => setItemDlg(null)} onSaved={() => { setItemDlg(null); loadItems(); }} />}
      {obsFor && <ObservationsDialog trigger={obsFor} onClose={() => { setObsFor(null); loadItems(); }} />}
    </div>
  );
};

// ---- Diary editor for one lesson ----
const DiaryEditor = ({ dogId, sid, onBack }) => {
  const [loaded, setLoaded] = useState(false);
  const [session, setSession] = useState(null);
  const [incompatible, setIncompatible] = useState(false);
  const [diaryId, setDiaryId] = useState(null);
  const [status, setStatus] = useState("empty");
  const [fields, setFields] = useState(EMPTY);
  const [saveState, setSaveState] = useState("idle");
  const [homework, setHomework] = useState([]);
  const [carryover, setCarryover] = useState([]);
  const [hwDlg, setHwDlg] = useState(null); // {item?}
  const [exitPrompt, setExitPrompt] = useState(false);

  const timer = useRef(null);
  const dirty = useRef(false);
  const fieldsRef = useRef(EMPTY);
  const versionRef = useRef(0);

  const reload = useCallback(async () => {
    const { data } = await apiGet(`/dogs/${dogId}/sessions/${sid}/lesson-diary`);
    setSession(data.session);
    setIncompatible(data.incompatible);
    setHomework(data.homework || []);
    setCarryover(data.carryover_homework || []);
    if (data.diary) {
      setDiaryId(data.diary.id);
      setStatus(data.diary.status);
      versionRef.current = data.diary.version;
      const f = { ...EMPTY, ...pick(data.diary, DIARY_KEYS), relevance: data.diary.relevance || "unspecified" };
      fieldsRef.current = f;
      setFields(f);
    } else {
      setDiaryId(null); setStatus("empty");
      versionRef.current = 0; fieldsRef.current = EMPTY; setFields(EMPTY);
    }
    dirty.current = false;
  }, [dogId, sid]);

  useEffect(() => { reload().finally(() => setLoaded(true)); }, [reload]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const doSave = async (f) => {
    setSaveState("saving");
    try {
      const { data } = await api.put(`/dogs/${dogId}/sessions/${sid}/lesson-diary`, { ...f, version: versionRef.current });
      if (data.exists && data.diary) {
        versionRef.current = data.diary.version;
        setDiaryId(data.diary.id);
        setStatus(data.diary.status);
      }
      dirty.current = false;
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      if (e?.response?.status === 409) { toast.error("Conflitto: il diario è stato ricaricato."); await reload(); }
      throw e;
    }
  };

  const setField = (k, v) => {
    const next = { ...fieldsRef.current, [k]: v };
    fieldsRef.current = next;
    setFields(next);
    dirty.current = true;
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { doSave(next).catch(() => {}); }, 800);
  };

  const flush = async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (dirty.current) await doSave(fieldsRef.current);
  };

  const handleBack = async () => {
    if (!dirty.current && !timer.current) return onBack();
    try { await flush(); onBack(); }
    catch { setExitPrompt(true); }
  };

  const hasContent = DIARY_MAIN.concat(DIARY_OPT, [["internal_notes"]]).some(([k]) => (fields[k] || "").trim());
  const canComplete = hasContent || homework.some((h) => hwValid(h) && h.status !== "cancelled");

  const complete = async () => {
    try {
      await flush();
      if (!diaryId) return toast.error("Aggiungi un contenuto prima di completare.");
      const { data } = await api.patch(`/lesson-diaries/${diaryId}/status`, { status: "completed", version: versionRef.current });
      versionRef.current = data.version; setStatus(data.status);
      toast.success("Diario completato");
    } catch (e) {
      if (e?.response?.status === 409) { await reload(); toast.error("Conflitto: diario ricaricato, riprova."); }
      else toast.error(apiErr(e));
    }
  };

  const addHomework = async (f) => {
    await flush();
    await api.post(`/dogs/${dogId}/sessions/${sid}/lesson-diary/homework`, f);
    toast.success("Compito aggiunto");
    await reload();
  };
  const editHomework = async (id, f) => {
    const target = homework.concat(carryover).find((h) => h.id === id);
    await api.put(`/homework/${id}`, { ...f, version: target?.version });
    toast.success("Compito aggiornato");
    await reload();
  };
  const setHwStatus = async (h, st) => {
    try { await api.patch(`/homework/${h.id}/status`, { status: st }); await reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!loaded) return <p className="text-sm text-muted-foreground" data-testid="diary-loading">Caricamento…</p>;

  return (
    <div className="space-y-4" data-testid="diary-editor">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" className="rounded-full" data-testid="diary-back" onClick={handleBack}><ArrowLeft className="h-4 w-4 mr-1" />Lezioni</Button>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs font-semibold ${SAVE_CLS[saveState]}`} data-testid="diary-save-state">{SAVE_LABEL[saveState]}</span>
          <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${DIARY_STATUS_CLS[status]}`} data-testid="diary-status-badge">{DIARY_STATUS[status]}</span>
        </div>
      </div>

      {session && (
        <div className="rounded-2xl border border-border bg-card p-3" data-testid="diary-session-head">
          <div className="font-semibold">{session.service_type || "Prestazione"}</div>
          <div className="text-xs text-muted-foreground">{fmtDate(session.date)}{session.start_time ? ` · ${session.start_time}` : ""}</div>
        </div>
      )}

      {incompatible && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="diary-incompatible-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">Questa prestazione non è più in stato «Svolta», ma il diario è stato conservato. Nessun dato è stato eliminato.</div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
        {DIARY_MAIN.map(([k, label]) => (
          <div key={k}><div className="text-sm font-medium">{label}</div>
            <AutoTextarea testid={`diary-field-${k}`} value={fields[k]} onChange={(v) => setField(k, v)} /></div>
        ))}
      </div>

      <details className="rounded-2xl border border-border bg-card p-4" data-testid="diary-optional">
        <summary className="text-sm font-semibold cursor-pointer">Campi professionali opzionali</summary>
        <div className="space-y-4 mt-3">
          {DIARY_OPT.map(([k, label]) => (
            <div key={k}><div className="text-sm font-medium">{label}</div>
              <AutoTextarea testid={`diary-field-${k}`} value={fields[k]} onChange={(v) => setField(k, v)} /></div>
          ))}
          <div>
            <div className="text-sm font-medium">Rilevanza della lezione</div>
            <select data-testid="diary-relevance" className={`${inputCls} mt-1`} value={fields.relevance} onChange={(e) => setField("relevance", e.target.value)}>
              {RELEVANCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-xs text-muted-foreground mt-1">La sola rilevanza non basta per completare il diario.</div>
          </div>
        </div>
      </details>

      <div className="rounded-2xl border border-slate-300 bg-slate-50 p-4" data-testid="diary-internal">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Lock className="h-3.5 w-3.5" />Note interne</div>
        <div className="text-xs text-slate-500 mb-1">Solo per te. Non destinate al proprietario.</div>
        <AutoTextarea testid="diary-field-internal_notes" value={fields.internal_notes} onChange={(v) => setField("internal_notes", v)} />
      </div>

      {/* Homework */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="homework-section">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-head text-base font-bold">Compiti a casa</h4>
          <Button size="sm" className="rounded-full" data-testid="homework-add-btn" onClick={() => setHwDlg({})}><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
        </div>
        {homework.length === 0
          ? <p className="text-sm text-muted-foreground">Nessun compito per questa lezione.</p>
          : <div className="space-y-2" data-testid="homework-list">{homework.map((h) => <HomeworkCard key={h.id} h={h} onEdit={(it) => setHwDlg({ item: it })} onStatus={setHwStatus} />)}</div>}

        {carryover.length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-semibold text-muted-foreground mb-2">Compiti attivi da lezioni precedenti</div>
            <div className="space-y-2" data-testid="carryover-list">{carryover.map((h) => <HomeworkCard key={h.id} h={h} carry onEdit={(it) => setHwDlg({ item: it })} onStatus={setHwStatus} />)}</div>
          </div>
        )}
      </div>

      <DiaryToRecord dogId={dogId} />

      <div className="sticky bottom-[64px] lg:bottom-0 z-50 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-t border-border flex items-center gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <span className="text-xs text-muted-foreground">{status === "completed" ? "Diario completato — resta modificabile" : status === "draft" ? "Bozza salvata automaticamente" : "Salvataggio automatico"}</span>
        <Button className="ml-auto rounded-full" data-testid="diary-complete-btn" disabled={!canComplete || status === "completed"} onClick={complete}>
          <CheckCircle2 className="h-4 w-4 mr-1" />{status === "completed" ? "Completato" : "Completa diario"}
        </Button>
      </div>

      {hwDlg && (
        <HomeworkForm
          item={hwDlg.item}
          onClose={() => setHwDlg(null)}
          onSubmit={(f) => (hwDlg.item ? editHomework(hwDlg.item.id, f) : addHomework(f))}
        />
      )}

      <AlertDialog open={exitPrompt} onOpenChange={setExitPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Salvataggio non riuscito</AlertDialogTitle>
            <AlertDialogDescription>Le ultime modifiche non sono state salvate. Vuoi riprovare o uscire senza salvare?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exit-retry">Riprova</AlertDialogCancel>
            <AlertDialogAction data-testid="exit-discard" onClick={() => { dirty.current = false; onBack(); }}>Esci senza salvare</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---- Lessons list ----
export const LessonsTab = ({ dogId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get("session");
  const [lessons, setLessons] = useState(null);
  const [selected, setSelected] = useState(sessionParam || null);
  useEffect(() => { setSelected(sessionParam || null); }, [sessionParam]);
  const load = useCallback(() => { apiGet(`/dogs/${dogId}/lessons`).then(({ data }) => setLessons(data)).catch(() => setLessons([])); }, [dogId]);
  useEffect(() => { if (!selected) load(); }, [selected, load]);
  const back = () => { setSelected(null); setSearchParams({ tab: "lezioni" }); };

  if (selected) return <DiaryEditor dogId={dogId} sid={selected} onBack={back} />;
  if (lessons === null) return <p className="text-sm text-muted-foreground">Caricamento…</p>;

  return (
    <div className="space-y-3" data-testid="lessons-tab">
      {lessons.length === 0
        ? <p className="text-sm text-muted-foreground" data-testid="lessons-empty">Nessuna prestazione svolta da documentare.</p>
        : lessons.map((l) => (
          <button key={l.session_id} type="button" data-testid={`lesson-card-${l.session_id}`} onClick={() => setSelected(l.session_id)}
            className="w-full text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold break-words">{l.service_type || "Prestazione"}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(l.date)}{l.start_time ? ` · ${l.start_time}` : ""}</div>
              </div>
              <span className={`text-xs font-semibold rounded-full px-2 py-0.5 shrink-0 ${DIARY_STATUS_CLS[l.diary_status]}`} data-testid={`lesson-status-${l.session_id}`}>{DIARY_STATUS[l.diary_status]}</span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
              {l.homework_count > 0 && <span>{l.homework_count} compito/i</span>}
              {l.incompatible && <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Prestazione non più «Svolta»</span>}
            </div>
          </button>
        ))}
    </div>
  );
};
